import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Grid,
  Divider
} from '@mui/material';


import RiskRatings from './RiskRatings';
import OverallDetails from './OverallDetails';


// Main component that accepts data as a prop
export default function SummaryOverall({ rawData, locationData }) {
  const [processedData, setProcessedData] = useState(null);
  const [error, setError] = useState(null);

  // The sentences for risk assessment, moved to be accessible by the function.
  const riskSentences = {
    // Urban status
    urban: {
      true: 'This area is within a defined urban zone,',
      false: 'This is a non-urban area,'
    },
    // Fuel risk contribution
    fuel: {
      '-2': 'and has significantly less fire-prone vegetation, greatly reducing the risk of fire spreading.',
      '-1': 'and has lower fuel hazards, which helps to mitigate fire risk.',
      '0': 'and has similar vegetation hazards to Pacific Palisades, presenting a comparable risk of fire spread.',
      '1': 'and has a high concentration of hazardous vegetation, making it more susceptible to a fast-moving fire.',
      '2': 'and has an extreme abundance of fire-prone vegetation, creating a very high risk of homes being lost.'
    },
    // Fire risk contribution
    fire: {
      '-2': 'Historical data and weather conditions indicate a much lower overall fire risk.',
      '-1': 'Historical data and weather conditions point to a lower overall fire risk.',
      '0': 'Historical data and weather conditions show a fire risk comparable to Pacific Palisades.',
      '1': 'Historical data and weather conditions suggest a higher overall fire risk.',
      '2': 'Historical data and weather conditions indicate an extreme overall fire risk.'
    },
    // Special mitigation or exacerbation phrases for combined risk
    mitigation: {
      'high_fuel_low_fire': 'However, a lower overall fire risk due to historical weather and climate patterns helps to mitigate the danger to homes.',
      'low_fuel_high_fire': 'However, a higher overall fire risk due to historical weather and climate patterns suggests that homes may still be at risk, despite the lower fuel load.',
    },
    // Summary conclusions
    conclusion: {
      low: 'Overall, the risk to homes in this area is considered low.',
      moderate: 'The combined risks place this area at a moderate risk of homes being lost.',
      same: 'The combined risks place this area at the same risk of Pacific Palisades.',
      high: 'The combination of factors places this area at a high risk of homes being lost.',
      very_high: 'The combined risk factors indicate a very high potential for homes to be lost in this area.'
    }
  };

  const getFuelRiskDetails = (data) => {
    const riskGroups = {
      5: { label: 'Very High Risk', items: [] },
      4: { label: 'High Risk', items: [] },
      3: { label: 'Moderate Risk', items: [] },
      2: { label: 'Low Risk', items: [] },
      1: { label: 'No Risk', items: [] },
    };

    // Filter out 'URBAN' and group data by 'value'
    data
      .filter((item) => item.category !== 'URBAN')
      .forEach((item) => {
        if (riskGroups[item.value]) {
          riskGroups[item.value].items.push(item);
        }
      });

    const sortedValues = [5, 4, 3, 2, 1];
    return (
      <Box>
        {sortedValues.map((value) => {
          const group = riskGroups[value];
          if (group.items.length > 0) {
            const formattedItems = group.items
              .map((item) => {
                if (item.category === 'BARREN/OTHER') {
                  return item.name;
                }
                const formattedCategory = item.category.charAt(0).toUpperCase() + item.category.slice(1).toLowerCase();
                return `${item.name} (${formattedCategory})`;
              })
              .join(', ');

            return (
              <Box key={value} sx={{ mb: 0 }}>
                <Typography variant="body1">
                  <strong>{group.label}:</strong> {formattedItems}
                </Typography>
              </Box>
            );
          }
          return null;
        })}
      </Box>
    );
  };

  /**
   * Processes the raw data object to generate headline, summary, description, and total score.
   * This function combines the logic from the original Node.js script's `generateContent` and `processFile`.
   * @param {object} data - The parsed JSON data for a specific location.
   * @returns {{...data, headline: string, summary: string, description: string, totalScore: number}} The processed data object.
   */
  const processData = (data) => {
    // Check for valid data structure
    if (!data || !data.fuel_risk || typeof data.fuel_risk.score !== 'number' ||
      !data.fire_risk || typeof data.fire_risk.score !== 'number') {
      throw new Error('Invalid data structure provided.');
    }

    const fuelScore = String(data.fuel_risk.score);
    const fireScore = String(data.fire_risk.score);
    const isUrban = data.urban;

    let title = '';
    let headline = '';
    let summary = '';
    let conclusion = '';
    let fire_department = '';

    // Add the fixed description
    const description = `In comparison to Pacific Palisades, which is a Very High Fire Hazard Severity Zone, this report highlights how local vegetation and long-term weather trends influence the fire danger profile of this location.`;

    const urbanSentence = riskSentences.urban[isUrban];
    const fuelSentence = riskSentences.fuel[fuelScore];
    const fireSentence = riskSentences.fire[fireScore];

    // Determine headline and conclusion based on the combined risk
    var intFuelScore = parseInt(fuelScore);
    var intFireScore = parseInt(fireScore);
    var totalScore = intFuelScore + intFireScore;

    if (intFuelScore > 0 && intFireScore == -1) {
      totalScore = 0;
    }

    if (totalScore <= -2) {
      title = 'Moderate Fire Risk Zone';
      headline = 'Significantly Safer Area Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.low;
    } else if (totalScore < 0) {
      title = 'High Fire Risk Zone';
      headline = 'Lower Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.moderate;
    } else if (totalScore === 0) {
      title = 'Very High Fire Risk Zone';
      headline = 'Similar Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.same;
    } else if (totalScore <= 2) {
      title = 'Extreme Fire Risk Zone';
      headline = 'Higher Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.high;
    } else {
      title = 'Maximum Fire Risk Zone';
      headline = 'Significantly Higher Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.very_high;
    }

    // Build the final summary paragraph
    summary += `${urbanSentence} ${fuelSentence} ${fireSentence}`;

    // Apply the new logic for interacting risks
    if ((fuelScore === '1' || fuelScore === '2') && (fireScore === '-1' || fireScore === '-2')) {
      summary += ` ${riskSentences.mitigation.high_fuel_low_fire}`;
      conclusion = 'This area is at a lower risk for homes because the low fire risk mitigates the high fuel risk.';
    } else if ((fuelScore === '-1' || fuelScore === '-2') && (fireScore === '1' || fireScore === '2')) {
      summary += ` ${riskSentences.mitigation.low_fuel_high_fire}`;
      conclusion = 'This area is at a higher risk for homes because the high fire risk exacerbates the low fuel risk.';
    }

    // Add the final conclusion
    summary += ` ${conclusion}`;

    if (locationData.county === "Los Angeles County" || locationData.county === "Ventura County") {
      fire_department = 'Regardless of excuses about budget, weather, or resources, the fire department in your area has a proven history of failure. It is advised that you remain prepared for your residence to be destroyed by fire.';
    }

    // Return the new data object with the added properties
    return {
      ...data,
      title,
      headline,
      summary,
      description,
      fire_department,
      totalScore
    };
  };


  const calculateFireRiskValue = (data, score) => {
    score = adjustScore(score);
    var percent = (data.order / data.total) * 20;

    switch (score) {
      case -2:
        percent += 0;
        break;
      case -1:
        percent += 20;
        break;
      case 0:
        percent += 40;
        break;
      case 1:
        percent += 60;
        break;
      case 2:
        percent += 80;
        break;
    }

    return percent;
  };

  // Use a useEffect hook to process the data whenever the prop changes.
  useEffect(() => {
    if (rawData) {
      try {
        const result = processData(rawData);
        setProcessedData(result);
        setError(null);
      } catch (e) {
        setError(e.message);
        setProcessedData(null);
      }
    } else {
      setProcessedData(null);
      setError(null);
    }
  }, [rawData]);

  const reportData = rawData;

  const adjustScore = (score) => {
    if (score < -2) {
      score = -2;
    }
    if (score > 2) {
      score = 2;
    }
    return score;
  };

  // Helper function to get the color based on score
  const getScoreColor = (score) => {
    score = adjustScore(score);
    switch (score) {
      case -2: return '#fcd34d'; // yellow-300
      case -1: return '#eab308'; // yellow-500
      case 0: return '#f97316'; // orange-500
      case 1: return '#dc2626'; // red-600
      case 2: return '#7f1d1d'; // red-900
      default: return '#cbd5e1'; // gray-300
    }
  };

  // Helper function to get the display text based on score
  const getScoreDisplay = (score) => {
    score = adjustScore(score);
    switch (score) {
      case -2: return 'Lower than Pacific Palisades';
      case -1: return 'Slightly Lower than Pacific Palisades';
      case 0: return 'Same as Pacific Palisades';
      case 1: return 'Slightly Higher than Pacific Palisades';
      case 2: return 'Higher than Pacific Palisades';
      default: return 'Unknown';
    }
  };

  const ScoreBar = ({ label, score, isLarge = false }) => {
    score = adjustScore(score);
    const minScore = -2,
      maxScore = 2;
    const percent = ((score - minScore) / (maxScore - minScore)) * 100;

    return (
      <Box sx={{ mb: isLarge ? 3 : 2 }}>
        <Typography variant={isLarge ? "h6" : "body2"}>
          <strong>{label}:</strong> {getScoreDisplay(score)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, percent))}
          sx={{
            height: isLarge ? 8 : 6,
            borderRadius: 5,
            backgroundColor: '#e5e7eb', // gray-200 for the track
            '& .MuiLinearProgress-bar': {
              backgroundColor: getScoreColor(score),
            },
          }}
        />
      </Box>
    );
  };

  const SummaryContent = () => {
    if (!processedData) return null;
    return (
        <Card sx={{ mb: 4 }}>
          <Box sx={{ bgcolor: getScoreColor(processedData.totalScore), color: "white", p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">
                <strong>Overall Fire and Fuel Risk:</strong> {getScoreDisplay(processedData.totalScore)}
              </Typography>
              <RiskRatings rank={adjustScore(processedData.totalScore) + 3} />
            </Box>
          </Box>
          <CardContent>
            <Box>
              <Typography variant="h6" color="text.secondary">
                Rank:
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{processedData.title}</strong>
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Summary:
              </Typography>
              <Typography variant="body1" gutterBottom>
                {processedData.headline}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Details:
              </Typography>
              <Typography variant="body1">
                {processedData.summary}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Prevention and Mitigation:
              </Typography>
              <Typography variant="body1">
                The stated purpose of Home Hardening, Defensible Space, and Zone 0 is wildfire protection, but in practice these measures shift accountability away from fire departments and leadership when homes are destroyed. 
                In areas where it has already been proven that the fire department cannot save homes during major fires, compliance becomes the new excuse. If your home burns, the blame will fall on you or your neighbors for not following the new rules, even though evidence shows these measures are largely ineffective. 
                For example, during the 2025 Palisades Fire, fully compliant structures (including modern code compliant homes as well as commerical structures like Ralphs and Gelsins) were destroyed while homes surrounded by ficus survived.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Recommendation:
              </Typography>
              <Typography variant="body1">
                Beyond standard emergency kits, it is recommended to invest in fire hydrant wrenches (if the water has not been shut off), generators (for electricty), pools (for water), pool pumps (to extinguish fires), and gas masks (to breathe) as part of your personal fire defense strategy. In some neighbors, communities have gone further by hiring private fire crews and maintaining dedicated water trucks, emphasizing the lack of confidence in public resources to provide sufficient protection during major fire events.
              </Typography>
              {processedData.fire_department && (
                <Typography variant="body1">
                  <br />
                  {processedData.fire_department}
                </Typography>
              )}
            </Box>
          </CardContent>
          <Divider sx={{ mb: 2 }} />

          <OverallDetails city={locationData.city} county={locationData.county} rawData={rawData}/>

        </Card>
    );
  };


  return (
    <div className="bg-gray-100 min-h-screen p-8 font-sans antialiased text-gray-800">
      <div className="max-w-3xl mx-auto bg-white shadow-xl rounded-xl p-8">
        <Box sx={{ pt: 2 }}>
          {error && <Typography color="error">{error}</Typography>}
          {processedData ? <SummaryContent /> : <p>Loading report...</p>}
        </Box>
      </div>
    </div>
  );
}
